import { z } from 'zod';

import { SIDE_EFFECTS } from '../risk.js';

/**
 * The approval wire - AGT-008, POL-004.
 *
 * **What is not here is the callback id.** It is how a run is resumed and it is
 * meaningful only to Lambda; a client that held one could resume a run it was
 * never asked about. The id on the wire is the approval row's, and the API
 * looks the callback up from it.
 *
 * The digest is not here either, for a smaller reason: a client that recomputed
 * it would need the arguments, and §14.3 says a person is shown a *summary* of
 * the action, not its payload.
 */
export const approvalSchema = z
  .object({
    id: z.string().uuid(),
    runId: z.string().uuid(),
    /** One sentence: the app, the action, the target - §14.3. */
    summary: z.string(),
    /** So a screen can weight the request without a second lookup. */
    sideEffect: z.enum(SIDE_EFFECTS),
    expiresAt: z.string(),
    createdAt: z.string(),
    /** Absent while it waits. Present once somebody has answered. */
    decision: z.enum(['APPROVED', 'REJECTED']).optional(),
    decidedAt: z.string().optional(),
  })
  .readonly();

export type Approval = z.infer<typeof approvalSchema>;

export const approvalListResponseSchema = z
  .object({ approvals: z.array(approvalSchema).readonly() })
  .readonly();

export type ApprovalListResponse = z.infer<typeof approvalListResponseSchema>;

/**
 * Two answers and no third.
 *
 * **Approve-once is the P0 behaviour**, so there is no "always allow" here.
 * `approval-engine.md`: persistent allow rules are admin-only and never apply
 * to financial or destructive actions - which is a policy engine and a role
 * check, neither of which exists.
 */
export const approvalDecisionRequestSchema = z
  .object({ decision: z.enum(['APPROVED', 'REJECTED']) })
  .readonly();

export type ApprovalDecisionRequest = z.infer<typeof approvalDecisionRequestSchema>;

export const approvalResponseSchema = z.object({ approval: approvalSchema }).readonly();

export type ApprovalResponse = z.infer<typeof approvalResponseSchema>;
